
























































































import { Watch, Component, Prop, Vue } from 'vue-property-decorator'
import { Referral } from '@/api/model/referral'
import { availableReferralChannels, ReferralChannel, ReferralNodeType, ReferralStatus } from '@/api/types'
import { Branch } from '@/api/model/branch'
import { ReferralNode } from '@/api/model/referral-node'
import { CompanyAPI, ReferralAPI, SourceAPI } from '@/api/backend-client'
import { v4 as uuid } from 'uuid'
import { computed } from '@vue/composition-api'
import { generateChannelOptions, InputSelectOption } from '@/helpers/options'
import { Source } from '@/api/model/source'

@Component
export default class CreateAdditionalReferralModal extends Vue {
    @Prop({ required: true }) readonly referral!: Referral
    @Prop({ type: Boolean, default: false }) value!: boolean
    selectedChannel: string = null
    selectedPipelineId: string = null
    visible: boolean = false
    loading: boolean = false
    selectedSource: Source = null
    selectedType: ReferralNodeType = null
    selectedBranch: Branch = null
    selectedFromBranch: Branch = null
    availableBranches: Branch[] = []
    outgoingReferrals: ReferralNode[] = []

    changeChannelLoading: boolean = true
    combinedSources: Source[] = []

    @Watch('value')
    onValueChanged(): void {
        this.visible = this.value
    }

    @Watch('visible')
    onVisibleChanged(): void {
        this.$emit('input', this.visible)
    }

    async mounted(): Promise<void> {
        await this.loadOutgoingReferrals()
        await this.loadSources()
        this.autoSelectBranch()
        this.autoShowIfReferralWasCreated()
        this.autoSelectSource()
        this.autoSelectChannel()
    }

    private autoSelectSource(): void {
        this.selectedSource = this.combinedSources.find(source => source.id === this.referral.newSource?.id)
    }

    private autoSelectChannel(): void {
        this.selectedChannel = availableReferralChannels().find(channel => channel === this.referral.channel)
    }

    get availableChannelOptions(): InputSelectOption[] {
        return generateChannelOptions()
    }

    autoShowIfReferralWasCreated(): void {
        if (window.keyvue.referralCreated) {
            window.keyvue.referralCreated = false

            if (confirm(this.$t('referral.create-additional.confirm').toString())) {
                this.showModal()
            }
        }
    }

    showModal(): void {
        this.visible = true
    }

    hideModal(): void {
        this.visible = false
    }

    autoSelectBranch(): void {
        const branches = this.$currentUser.getAvailableBranches()

        if (branches.length === 1) {
            this.selectedFromBranch = branches[0]
        }
    }

    async createAdditionalReferral(): Promise<void> {
        this.loading = true
        await ReferralAPI.create({
            ...this.referral,

            contact: this.referral.contact.resourceIdentifier,
            company: this.$currentCompany.resourceIdentifier,
            id: uuid(),
            type: this.selectedType,
            referredBy: this.$currentUser.resourceIdentifier,
            referredByBranch: this.selectedFromBranch.resourceIdentifier,
            assignedTo: null,
            assignedToBranch: Branch.fromData(this.selectedBranch).resourceIdentifier,
            status: ReferralStatus.NEW,
            newSource: this.selectedSource?.resourceIdentifier,
            channel: this.selectedChannel,
            statusReason: null,
            nextActionAt: null,
            appointment: null,
            appointmentLocation: null,
            orderValue: null,
            leadCommission: null,
            nettoOrderValue: null,
            transportDate: null,
            productionDate: null,
            score: 0,

        } as unknown as Referral)
        this.loading = false
        this.hideModal()
    }

    async loadOutgoingReferrals() {
        this.loading = true
        this.outgoingReferrals = await CompanyAPI.getOutgoingReferrals()
        this.loading = false
    }

    @Watch('selectedBranch')
    async loadSources(): Promise<void> {
        this.changeChannelLoading = true
        try {
            if (!this.selectedBranch) {
                this.combinedSources.splice(0)
                return
            }
            const sources = await SourceAPI.getAll({ company: this.selectedBranch.company.id, active: 1 })
            this.combinedSources.splice(0)
            this.combinedSources.push(...sources)
        } finally {
            this.changeChannelLoading = false
        }
    }

    @Watch('selectedType')
    async onSelectedTypeChanged() {
        this.loading = true
        this.selectedBranch = null
        this.availableBranches = []
        this.availableBranches = (await Promise.all(this.outgoingReferrals

            // Keep only active referral nodes.
            .filter((node: ReferralNode) => node.active === true)

            // Keep only desired referral type nodes.
            .filter((node: ReferralNode) => node.type === this.selectedType)

            // Create a map of branches.
            .map(async (node: ReferralNode) => {
                const company = await CompanyAPI.get(node.target.id)
                return company.getAvailableBranches()

                    // Keep branches that accept the referral type.
                    .filter((branch: Branch) => branch.acceptsReferralType(this.selectedType))

                    // Assign branch company with proper value.
                    .map((branch: Branch) => ({ ...branch, company }))

                    // Sort alphabetically.
                    .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
            })))

            // Flatten promise.all results.
            .flat() as Branch[]

        // Auto-select branch if only one result.
        if (this.availableBranches.length === 1) {
            this.selectedBranch = this.availableBranches[0]
        }

        this.loading = false
    }

    setSelectedSource(sourceId: string): void {
        this.selectedSource = this.combinedSources.find(source => source.id === sourceId)
    }

    get availableReferralTypes() {
        return this.$currentCompany.handledReferralTypes.map(type => {
            return {
                label: this.$t(`referral.type.${type}`),
                value: type,
            }
        })
    }

    get availableFromBranches() {
        return this.$currentUser.getAvailableBranches()
    }

    get sourceOptions(): InputSelectOption[] {
        return this.$currentCompany.activeSources
            .filter(source => source.channel === ReferralChannel.USER)
            .map(source => ({
                label: source.name,
                value: source.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }
}
